import { useState } from "react";
import { useUpdateProfileMutation } from "../../store/api/authApi";
import { User } from "../../types";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";

interface ProfileFormProps {
	user: User;
	onCancel: () => void;
	onSuccess: () => void;
}

interface UpdateProfileData {
	firstName: string;
	lastName: string;
	phone?: string;
	email_notifications?: boolean;
	sms_notifications?: boolean;
	// Tenant fields
	emergency_contact_name?: string;
	emergency_contact_phone?: string;
	emergency_contact_relation?: string;
	// Technician fields
	primary_specialty?: string;
	experience?: string;
	license_number?: string;
	// Agent fields
	brokerage_name?: string;
	service_areas?: string;
	// Admin fields
	company_name?: string;
	job_title?: string;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
	user,
	onCancel,
	onSuccess,
}) => {
	const [updateProfile, { isLoading }] = useUpdateProfileMutation();
	const [formData, setFormData] = useState<UpdateProfileData>({
		firstName: user.first_name,
		lastName: user.last_name,
		phone: user.phone || "",
		email_notifications: user.email_notifications ?? true,
		sms_notifications: user.sms_notifications ?? false,
		// Tenant fields
		emergency_contact_name: user.emergency_contact_name || "",
		emergency_contact_phone: user.emergency_contact_phone || "",
		emergency_contact_relation: user.emergency_contact_relation || "",
		// Technician fields
		primary_specialty: user.primary_specialty || "",
		experience: user.experience || "",
		license_number: user.license_number || "",
		// Agent fields
		brokerage_name: user.brokerage_name || "",
		service_areas: user.service_areas || "",
		// Admin fields
		company_name: user.company_name || "",
		job_title: user.job_title || "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const handleInputChange = (field: keyof UpdateProfileData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName.trim()) {
			newErrors.firstName = "First name is required";
		}

		if (!formData.lastName.trim()) {
			newErrors.lastName = "Last name is required";
		}

		// Phone is optional, but validate format if provided
		if (
			formData.phone &&
			formData.phone.trim() &&
			!/^\+?[\d\s\-\(\)]+$/.test(formData.phone)
		) {
			newErrors.phone = "Please enter a valid phone number";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) {
			return;
		}

		setErrors({});

		try {
			await updateProfile({
				firstName: formData.firstName,
				lastName: formData.lastName,
				phone: formData.phone,
				email_notifications: true,
				sms_notifications: formData.sms_notifications,
				// Tenant fields
				emergency_contact_name: formData.emergency_contact_name,
				emergency_contact_phone: formData.emergency_contact_phone,
				emergency_contact_relation: formData.emergency_contact_relation,
				// Technician fields
				primary_specialty: formData.primary_specialty,
				experience: formData.experience,
				license_number: formData.license_number,
				// Agent fields
				brokerage_name: formData.brokerage_name,
				service_areas: formData.service_areas,
				// Admin fields
				company_name: formData.company_name,
				job_title: formData.job_title,
			}).unwrap();

			onSuccess();
		} catch (error: any) {
			console.error("Profile update error:", error);

			if (error?.data?.error) {
				setErrors({ submit: error.data.error });
			} else if (error?.data?.first_name) {
				setErrors({
					firstName: Array.isArray(error.data.first_name)
						? error.data.first_name[0]
						: error.data.first_name,
				});
			} else if (error?.data?.last_name) {
				setErrors({
					lastName: Array.isArray(error.data.last_name)
						? error.data.last_name[0]
						: error.data.last_name,
				});
			} else if (error?.data?.phone) {
				setErrors({
					phone: Array.isArray(error.data.phone)
						? error.data.phone[0]
						: error.data.phone,
				});
			} else {
				setErrors({
					submit: "Failed to update profile. Please try again.",
				});
			}
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-6">
			{/* Basic Information */}
			<div>
				<h3 className="text-lg font-semibold text-foreground mb-4">
					Basic Information
				</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<Input
						label="First Name"
						type="text"
						value={formData.firstName}
						onChange={(e) => handleInputChange("firstName", e.target.value)}
						error={errors.firstName}
						required
					/>

					<Input
						label="Last Name"
						type="text"
						value={formData.lastName}
						onChange={(e) => handleInputChange("lastName", e.target.value)}
						error={errors.lastName}
						required
					/>

					<Input
						label="Phone Number"
						type="tel"
						value={formData.phone}
						onChange={(e) => handleInputChange("phone", e.target.value)}
						error={errors.phone}
						description="Optional - for important account updates"
					/>
				</div>
			</div>

			{/* Notification Preferences */}
			<div className="pt-4 border-t border-border">
				<h3 className="text-lg font-semibold text-foreground mb-4">
					Notification Preferences
				</h3>
				<div className="space-y-4">
					{/* Email notifications are always enabled */}
					<div className="flex items-center space-x-3 opacity-75">
						<input
							type="checkbox"
							checked={true}
							disabled
							className="w-4 h-4 text-primary bg-white border-gray-300 rounded cursor-not-allowed"
						/>
						<div>
							<div className="text-sm font-medium text-foreground">
								Email Notifications
							</div>
							<div className="text-xs text-muted-foreground">
								Always enabled - receive important updates via email
							</div>
						</div>
					</div>

					<label className="flex items-center space-x-3 cursor-pointer">
						<input
							type="checkbox"
							checked={formData.sms_notifications}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									sms_notifications: e.target.checked,
								}))
							}
							className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary focus:ring-2"
						/>
						<div>
							<div className="text-sm font-medium text-foreground">
								SMS Notifications
							</div>
							<div className="text-xs text-muted-foreground">
								Receive urgent alerts via text message
							</div>
						</div>
					</label>
				</div>
			</div>

			{/* Tenant-specific fields */}
			{user.role === "tenant" && (
				<div className="pt-4 border-t border-border">
					<h3 className="text-lg font-semibold text-foreground mb-4">
						Emergency Contact
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<Input
							label="Contact Name"
							type="text"
							value={formData.emergency_contact_name}
							onChange={(e) =>
								handleInputChange("emergency_contact_name", e.target.value)
							}
							description="Name of your emergency contact"
						/>

						<Input
							label="Contact Phone"
							type="tel"
							value={formData.emergency_contact_phone}
							onChange={(e) =>
								handleInputChange("emergency_contact_phone", e.target.value)
							}
							description="Emergency contact phone number"
						/>

						<Input
							label="Relationship"
							type="text"
							value={formData.emergency_contact_relation}
							onChange={(e) =>
								handleInputChange("emergency_contact_relation", e.target.value)
							}
							description="e.g., Spouse, Parent, Sibling"
						/>
					</div>
				</div>
			)}

			{/* Technician-specific fields */}
			{user.role === "technician" && (
				<div className="pt-4 border-t border-border">
					<h3 className="text-lg font-semibold text-foreground mb-4">
						Professional Information
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<Input
							label="Primary Specialty"
							type="text"
							value={formData.primary_specialty}
							onChange={(e) =>
								handleInputChange("primary_specialty", e.target.value)
							}
							description="e.g., Plumbing, Electrical, HVAC"
						/>

						<Input
							label="Years of Experience"
							type="text"
							value={formData.experience}
							onChange={(e) => handleInputChange("experience", e.target.value)}
							description="e.g., 5 years, 10+ years"
						/>

						<Input
							label="License Number"
							type="text"
							value={formData.license_number}
							onChange={(e) =>
								handleInputChange("license_number", e.target.value)
							}
							description="Professional license or certification number"
						/>
					</div>
				</div>
			)}

			{/* Agent-specific fields */}
			{user.role === "agent" && (
				<div className="pt-4 border-t border-border">
					<h3 className="text-lg font-semibold text-foreground mb-4">
						Business Information
					</h3>
					<div className="grid grid-cols-1 gap-6">
						<Input
							label="Brokerage Name"
							type="text"
							value={formData.brokerage_name}
							onChange={(e) =>
								handleInputChange("brokerage_name", e.target.value)
							}
							description="Name of your real estate brokerage"
						/>

						<Input
							label="Service Areas"
							type="text"
							value={formData.service_areas}
							onChange={(e) =>
								handleInputChange("service_areas", e.target.value)
							}
							description="Cities or regions you serve (comma-separated)"
						/>
					</div>
				</div>
			)}

			{/* Admin-specific fields */}
			{user.role === "admin" && (
				<div className="pt-4 border-t border-border">
					<h3 className="text-lg font-semibold text-foreground mb-4">
						Company Information
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<Input
							label="Company Name"
							type="text"
							value={formData.company_name}
							onChange={(e) =>
								handleInputChange("company_name", e.target.value)
							}
							description="Your organization name"
						/>

						<Input
							label="Job Title"
							type="text"
							value={formData.job_title}
							onChange={(e) => handleInputChange("job_title", e.target.value)}
							description="Your position in the company"
						/>
					</div>
				</div>
			)}

			{errors.submit && (
				<div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
					{errors.submit}
				</div>
			)}

			<div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					disabled={isLoading}
				>
					Cancel
				</Button>
				<Button type="submit" disabled={isLoading}>
					{isLoading ? (
						<>
							<Icon name="Loader2" size={16} className="mr-2 animate-spin" />
							Saving...
						</>
					) : (
						<>
							<Icon name="Save" size={16} className="mr-2" />
							Save Changes
						</>
					)}
				</Button>
			</div>
		</form>
	);
};

export default ProfileForm;
